import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { FELD_KLASSE } from '@/lib/feldKlassen';

// „Wohin gehört es?" — ein unzugeordneter Wareneingang bekommt
// nachträglich seinen Weg (A60).
//
// WARUM SIE NICHT IN WegFrage GEHÖRT
//   WegFrage stellt eine andere Frage. Dort ist gerade ein Eingang im
//   Entstehen und der Server weigert sich zu raten, WEM er gehört; hier
//   ist er längst gebucht und liegt seit Wochen unzugeordnet da. Der
//   sichtbarste Unterschied: Dort gehört „Weiß ich nicht" dazu, hier
//   wäre es sinnlos — das IST der Zustand, aus dem man kommt.
//
// ES WIRD NICHTS GELÖSCHT UND NICHTS DAZUGEBUCHT
//   Die eingetroffene Gesamtmenge der Position bleibt gleich. Es
//   verschiebt sich nur, wo sie steht. Deshalb nennt der Text „umhängen"
//   und nicht „buchen".
//
// Die Liste zeigt NUR Wege mit Platz (menge > menge_eingegangen). Über
// einen Weg kann nicht mehr eingetroffen sein, als über ihn bestellt
// wurde; ein Eintrag ohne Platz wäre ein Knopf, der sicher abgewiesen
// wird. Dieselbe Grenze zieht der Server (`exceeds_weg`).

export type UmhaengenZiel = {
  beschaffung_id: number;
  label: string;
  /** Platz in diesem Weg: menge − menge_eingegangen. */
  offen: number;
};

type Props = {
  offen: boolean;
  titel: string;
  /** Wieviel unzugeordnet dasteht. */
  ohneWeg: number;
  ziele: UmhaengenZiel[];
  onWahl: (beschaffungId: number, menge: number) => void;
  onAbbruch: () => void;
};

export function UmhaengenFrage({ offen, titel, ohneWeg, ziele, onWahl, onAbbruch }: Props) {
  // Bei genau einem unzugeordneten Exemplar gibt es nichts zu wählen —
  // dann fragt der Dialog nur nach dem Weg, und das Feld erscheint gar
  // nicht. Eine Menge einzutippen, die nur einen Wert haben kann, ist
  // ein Tipp ohne Auskunft.
  const mitMenge = ohneWeg > 1;
  const [mengeText, setMengeText] = useState('');

  /**
   * Was tatsächlich umgehängt wird.
   *
   * Vorgabe ist das Kleinere aus „was unzugeordnet dasteht" und „was in
   * diesen Weg passt" — mehr würde der Server ablehnen, und ein
   * Vorschlag, der sicher scheitert, ist keiner. Deshalb hängt die
   * Vorgabe am gewählten Ziel und nicht am Dialog.
   */
  function mengeFuer(ziel: UmhaengenZiel): number {
    const vorgabe = Math.min(ohneWeg, ziel.offen);
    if (!mitMenge) return vorgabe;
    const getippt = Number.parseInt(mengeText, 10);
    if (!Number.isFinite(getippt) || getippt < 1) return vorgabe;
    return Math.min(getippt, vorgabe);
  }

  return (
    <Dialog.Root open={offen} onOpenChange={(o) => !o && onAbbruch()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-ink/30" />
        <Dialog.Content
          // Fallstrick 13: Radix hält body-Portale für „außerhalb".
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          aria-describedby={undefined}
          data-ueber-tour=""
          className="dialog-mobil-unten fixed left-1/2 top-1/2 z-[60] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <Dialog.Title className="font-serif text-lg text-ink">Wohin gehört es?</Dialog.Title>
          <p className="mt-2 text-sm text-ink-soft">
            Von „{titel}“ {ohneWeg === 1 ? 'ist ein Exemplar' : `sind ${ohneWeg} Exemplare`}{' '}
            eingetroffen, ohne dass ein Weg dazu bekannt ist. Zu welchem{' '}
            {ohneWeg === 1 ? 'gehört es' : 'gehören sie'}?
          </p>

          {mitMenge && (
            <div className="mt-4">
              <label htmlFor="umhaengen-menge" className="block text-xs text-ink-soft">
                Wieviele davon?
              </label>
              <input
                id="umhaengen-menge"
                type="text"
                // Reine Ziffern, also ist `numeric` hier richtig
                // (Fallstrick 18).
                inputMode="numeric"
                value={mengeText}
                onChange={(e) => setMengeText(e.target.value.replace(/\D/g, ''))}
                placeholder={String(ohneWeg)}
                className={`mt-1 w-24 tabular-nums ${FELD_KLASSE}`}
              />
              <p className="mt-1 text-xs text-ink-faint">
                Leer heißt: soviel, wie in den gewählten Weg passt.
              </p>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {ziele.map((z) => (
              <button
                key={z.beschaffung_id}
                type="button"
                onClick={() => onWahl(z.beschaffung_id, mengeFuer(z))}
                className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-md border border-line bg-cream-warm px-3 text-sm text-ink hover:border-akzent hover:text-akzent"
              >
                <span className="min-w-0 truncate">{z.label}</span>
                <span className="shrink-0 tabular-nums text-ink-faint">{z.offen} offen</span>
              </button>
            ))}
          </div>

          {/* KEIN „Weiß ich nicht": Genau dort steht der Eingang gerade. */}
          <button
            type="button"
            onClick={onAbbruch}
            className="mt-3 min-h-[44px] w-full rounded-md px-3 text-sm text-ink-faint hover:text-coral"
          >
            Abbrechen
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
